// src/pages/AllMiniTools.js
import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { 
    IoImageOutline, 
    IoFilmOutline, 
    IoChatbubblesOutline, 
    IoSwapHorizontalOutline,
    IoCalendarOutline,
    IoSearchCircleOutline
} from 'react-icons/io5';
import heroBg from '../assets/images/all-mini-tools-background.png';

// --- Page-Specific Data ---
const seoData = {
  title: "All Free AI Mini-Tools | Oolook",
  description: "Explore a complete suite of free, AI-powered mini-tools for creators and marketers. From background removers to caption generators, streamline your workflow with Oolook.",
  image: 'https://oolook.in/og/all-mini-tools.png',
  schemas: [{
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Oolook's Free AI Mini-Tools",
    "description": "A directory of free, AI-powered tools for content creation, file conversion, and lead generation.",
    "url": "https://oolook.in/mini-tools",
  }]
};

const tools = [
  { icon: IoImageOutline, title: "Image BG Remover", description: "Instantly remove image backgrounds.", to: "/bg-remover" },
  { icon: IoFilmOutline, title: "Video BG Remover", description: "Remove video backgrounds without a green screen.", to: "/video-bg-remover" },
  { icon: IoChatbubblesOutline, title: "AI Caption Generator", description: "Generate engaging captions and hashtags.", to: "/caption-generator" },
  { icon: IoSwapHorizontalOutline, title: "Any-to-Any Converter", description: "Convert images, videos, and documents.", to: "/any-to-any-converter" },
  { icon: IoCalendarOutline, title: "Social Calendar", description: "Visually plan your content schedule.", to: "/social-calendar" },
  { icon: IoSearchCircleOutline, title: "Ozone Lead Finder", description: "Extract contacts from any website.", to: "/ozone" },
  // Add more tools here as they are built to expand the grid
];

const AllMiniTools = () => {
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        image={seoData.image}
        schemas={seoData.schemas}
      />
      <main className="bg-dark-bg text-dark-text-secondary font-sans animate-fade-in">
        <div className="relative isolate">
          {/* Full Screen Hero Section */}
          <section className="relative h-screen flex flex-col items-center justify-center text-center overflow-hidden px-6">
            <div className="hero-bg-image-container">
              <img src={heroBg} alt="Abstract toolkit background" className="w-full h-full object-cover animate-pan-zoom" />
            </div>
            <div className="relative z-10 mx-auto max-w-4xl">
              <h1 className="text-4xl font-extrabold tracking-tight text-dark-text-primary sm:text-6xl">
                Your Creative Toolkit, Supercharged
              </h1>
              <p className="mt-6 text-lg leading-8 text-dark-text-secondary">
                A complete suite of powerful, AI-driven mini-tools designed to handle the repetitive tasks, so you can focus on creativity. All free to use.
              </p>
            </div>
          </section>

          {/* Tools Grid Section */}
          <section className="py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {tools.map((tool) => (
                  <Link
                    key={tool.title}
                    to={tool.to}
                    className="group flex flex-col items-start p-8 bg-dark-card/40 backdrop-blur-lg rounded-2xl border border-white/10 transform-gpu transition-all duration-300 ease-in-out hover:scale-105 hover:border-dark-accent-start/50 hover:shadow-2xl hover:shadow-dark-accent-start/10"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-dark-accent-start to-dark-accent-end mb-6">
                      <tool.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-semibold text-dark-text-primary group-hover:text-white">
                      {tool.title}
                    </h3>
                    <p className="mt-2 text-sm text-dark-text-secondary">
                      {tool.description}
                    </p>
                    <p className="mt-6 text-sm font-semibold text-dark-accent-start group-hover:text-white">
                      Use Tool &rarr;
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default AllMiniTools;